import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ScanSettingsPage from "./ScanSettingsPage";

/**
 * The orchestrator's own settings, in the settings window.
 * ---------------------------------------------------------------------------
 * All three of its tabs live here now - what the estate is scanned with, the
 * proxy it goes through, the certificates it trusts - because they are one
 * thing to the person setting them: how the orchestrator works.
 *
 * Two awkward facts, handled rather than explained away:
 *
 *   The settings come over the scanner's own connection, which the Scan page
 *   is what brings up. So opening this group takes the page behind the window
 *   to Scan - the window itself is outside the routed part of the page and
 *   stays exactly where it is, and by the time someone has read the first
 *   line the values are there.
 *
 *   And the orchestrator may simply not be running, in which case there is
 *   nothing to configure and nothing to save. Then this says so, rather than
 *   showing empty fields that would write nowhere.
 * ---------------------------------------------------------------------------
 */

/* How long to let the orchestrator answer before saying it has not. Long
   enough for the Scan page to mount and the socket to open on a cold start. */
const PATIENCE = 9000;

export default function OrchestratorSettings({ isDarkMode, connected, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [answered, setAnswered] = useState(false);
  const [waited, setWaited] = useState(false);
  const went = useRef(false);

  /* Behind the window, to the page that owns the connection - and before the
     settings are drawn at all, not alongside them. The configuration comes
     over the scanner's own connection, which the Scan page is what brings up;
     showing the form first means showing a form with nothing in it for as long
     as that takes. Once, so a second navigation cannot arrive while someone is
     reading. */
  const onScan = /^\/scan/.test(location.pathname);

  useEffect(() => {
    if (went.current) return;
    went.current = true;
    if (!onScan) navigate("/scan");
  }, [navigate, onScan]);

  /* The clock on "is it answering" starts when the page it answers from is
     open, not when the window was. */
  useEffect(() => {
    if (!onScan) return undefined;
    const at = setTimeout(() => setWaited(true), PATIENCE);
    return () => clearTimeout(at);
  }, [onScan]);

  const ink = isDarkMode ? "rgba(245,239,230,0.9)" : "rgba(17,17,17,0.9)";
  const dim = isDarkMode ? "rgba(245,239,230,0.5)" : "rgba(17,17,17,0.55)";
  const hair = isDarkMode ? "rgba(245,239,230,0.12)" : "rgba(17,17,17,0.12)";

  /* Still on the way to Scan: nothing is drawn yet, because what would be
     drawn is a form that cannot be filled in. */
  if (!onScan) {
    return (
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6, color: dim }}>
        Opening the scanner…
      </p>
    );
  }

  /* Nothing to show yet: either the connection is down, or it is up and the
     orchestrator has not answered within the time it takes to answer. */
  const missing = !answered && (connected === false || waited);

  if (missing) {
    return (
      <div style={{ maxWidth: 520, color: ink, fontFamily: "Inter, system-ui, sans-serif" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f6400d" }} />
          <span style={{ fontSize: 14, fontWeight: 600 }}>Connect the orchestrator</span>
        </div>

        <p style={{ margin: "0 0 16px", fontSize: 13, lineHeight: 1.6, color: dim }}>
          These settings are the orchestrator&apos;s own, and it is not answering:
          {connected === false
            ? " this browser has no connection to it."
            : " the connection is up but it has not reported its configuration."}
          {" "}Start it, or connect to it from the Scan page, and they will be here.
        </p>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={() => { onClose(); navigate("/scan"); }}
            style={{
              height: 32, padding: "0 16px", borderRadius: 9,
              border: "1px solid rgb(246,64,13)", background: "rgb(246,64,13)", color: "#fff",
              fontFamily: "inherit", fontSize: 13, cursor: "pointer",
            }}
          >
            Go to Scan
          </button>
          <button
            type="button"
            onClick={() => { setWaited(false); setTimeout(() => setWaited(true), PATIENCE); }}
            style={{
              height: 32, padding: "0 14px", borderRadius: 9,
              border: `1px solid ${hair}`, background: "transparent", color: ink,
              fontFamily: "inherit", fontSize: 13, cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <ScanSettingsPage
      inline
      isDarkMode={isDarkMode}
      openAddIndexPage
      setOpenAddIndexPage={(v) => { if (!v) onClose(); }}
      tabs={["global", "proxy", "certs"]}
      onConfig={() => setAnswered(true)}
    />
  );
}
